import React from 'react';
import { RouteComponentProps } from '@reach/router';
import styled from 'styled-components';
import ViewContainer from 'components/ViewContainer';
import Header from 'components/Header';

const TermsOfAgreement: React.FC<RouteComponentProps> = (): JSX.Element => (
   <ViewContainer>
      <PageHeader>NEUVANA END USER LICENSE AGREEMENT</PageHeader>
      <TermsContainer>
         <Paragraph>
            PLEASE REVIEW THIS END USER LICENSE AGREEMENT (“<b>EULA</b>”) CAREFULLY. THIS EULA IS A BINDING LEGAL AGREEMENT BETWEEN NEUVANA LLC (“
            <b>NEUVANA</b>“) AND YOU AND, IF APPLICABLE, THE COMPANY OR OTHER LEGAL ENTITY YOU REPRESENT (COLLECTIVELY, “<b>YOU</b>“) REGARDING THE
            SOFTWARE THAT YOU ARE INSTALLING, DOWNLOADING, COPYING, ACCESSING OR USING (INCLUDING ANY FUTURE UPDATES, UPGRADES OR VERSIONS OF THE
            SOFTWARE) (COLLECTIVELY, THE “<b>SOFTWARE</b>“), AND ANY ASSOCIATED NEUVANA SERVICES AND ACCOMPANYING DOCUMENTS, INFORMATION, MANUALS OR
            OTHER RELATED MATERIALS PROVIDED BY NEUVANA AS PART OF, OR IN CONNECTION WITH THE SOFTWARE.
         </Paragraph>
         <Paragraph bold={true}>
            THE SOFTWARE AND THE SERVICES ARE FOR INFORMATION PURPOSES ONLY AND ARE NOT INTENDED TO BE USED FOR ANY OTHER PURPOSE, INCLUDING MEDICAL
            OR DIAGNOSTIC-RELATED PURPOSES.
         </Paragraph>
         <Paragraph bold={true}>
            THE SOFTWARE, USER INFORMATION, USER INTERFACE, SUPPORT, COMMUNICATION AND AGREEMENT, ARE ONLY PROVIDED IN CERTAIN LANGUAGES. DO NOT USE
            THE XEN OR OTHER NEUVANA PRODUCTS UNLESS YOU CAN FLUENTLY READ AND UNDERSTAND ONE OF THE AVAILABLE LANGUAGES. FURTHER, DO NOT INSTALL,
            DOWNLOAD OR USE THIS SOFTWARE UNLESS YOU CAN FLUENTLY READ AND UNDERSTAND THIS AGREEMENT.
         </Paragraph>
         <Paragraph bold={true}>EULA TERMS</Paragraph>
         <Paragraph>
            BY INSTALLING, DOWNLOADING, COPYING, ACCESSING OR USING THE SOFTWARE, OR OTHERWISE ACCEPTING THIS EULA, OR ‘CLICKING’ ACCEPT (IF
            APPLICABLE), YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD AND AGREE TO BE BOUND BY THE TERMS OF THIS EULA. IF YOU DO NOT UNDERSTAND OR
            AGREE, YOU MUST NOT INSTALL, DOWNLOAD, COPY, ACCESS OR USE THE SOFTWARE.
         </Paragraph>
         <OL>
            <li>
               <b>LICENSE GRANT AND RESTRICTIONS</b>
            </li>
            <OL>
               <li>
                  <Paragraph>
                     <b>Parties: </b>This Agreement is between YOU and NEUVANA only, and not Apple, Inc. (“Apple”). Notwithstanding the foregoing, you
                     acknowledge that Apple and its subsidiaries are third party beneficiaries of this Agreement and Apple has the right to enforce
                     this Agreement against you. NEUVANA, not Apple, is solely responsible for the NEUVANA App and its content.
                  </Paragraph>
               </li>
               <li>
                  <Paragraph>
                     <b>License Grant: </b>NEUVANA grants you a limited, personal, non-transferable, non-sub-licensable, and non-exclusive license to
                     install and use a single copy of the Software for your information and non-commercial use as part of the product or hardware
                     supplied by or on behalf of NEUVANA in which the Software is contained or for which it is provided, together with any applicable
                     NEUVANA Services (the “<b>License</b>”).
                  </Paragraph>
               </li>
               <li>
                  <b>Restrictions Including User-Generated Content</b>
                  <OL type="a">
                     <li>
                        <Paragraph>
                           With respect to the Software and NEUVANA Services, you will not (and will not allow any third party to): (a) exceed the
                           scope and purpose of the License grant above; (b) remove or alter any copyright notice or any other notices, (c) modify or
                           create derivative works; (d) reverse engineer or attempt to discover any source code or underlying ideas or algorithms; (d)
                           provide, lease, lend, sublicense, use for timesharing or otherwise use or allow the use of it for the benefit of any third
                           party (including minors, if applicable) unless you take full responsibility for ensuring that its use by anyone else will
                           comply with the terms of this EULA; or (e) use, or allow the use, transfer, transmission, export, or re-export of it or
                           portion thereof in violation of any export control laws or regulations, or in violation of any laws or regulations.
                        </Paragraph>
                     </li>
                     <li>
                        <Paragraph>
                           For the purposes of this EULA, “<b>User Generated Content</b>” means any text, feedback, ideas, suggestions, documents,
                           proposals, photographs or other data and information you or anyone acting on your behalf submits to NEUVANA including,
                           without limitation, through online and mobile services made available by or on behalf of NEUVANA from time to time
                           including, but not limited to, certain websites, widgets, computer programs, platforms, and mobile applications
                           (collectively, the “<b>NEUVANA Services</b>”). You represent that your User Generated Content: (a) will not infringe any
                           third party’s copyright, patent, trademark, trade secret or other proprietary rights, or rights of privacy; (b) will not
                           violate any law, statute, ordinance or regulation; (c) will not be obscene or contain child pornography; and (d) will not
                           be defamatory, unlawfully threatening or harassing, harmful to minors or others in any way, or otherwise offensive or
                           inappropriate.
                        </Paragraph>
                     </li>
                     <li>
                        <Paragraph>
                           You agree to indemnify, hold harmless and defend NEUVANA and its licensors from and against any claims or suits, including
                           attorneys’ fees and expenses, which arise or result from any breach of this EULA by you or by another who has access to or
                           use of the Software, NEUVANA Services or User Generated Content through you.
                        </Paragraph>
                     </li>
                  </OL>
               </li>
               <li>
                  <Paragraph>
                     <b>License Grant – Personally Identifiable User Generated Data: </b>For the purposes of this EULA, “
                     <b>Personally Identifiable User Generated Data</b>” means User Generated Content that identifies you and is considered personal
                     information in accordance with applicable privacy and data protection laws. For clarity, Personally Identifiable User Generated
                     Data does not include de-identified and/or aggregated information. You own your Personally Identifiable User Generated Data;
                     NEUVANA can’t use it without your consent. You grant to NEUVANA a revocable, non-exclusive, worldwide, royalty-free license, to
                     reproduce, distribute, transmit, publicly perform, publicly display, digitally perform, modify, create derivative works of, and
                     otherwise use Personally Identifiable User Generated Data in any manner and at any time only so far as required to deliver
                     Software, NEUVANA products and NEUVANA Services. You waive any rights of publicity, moral or other similar such right with
                     respect to Personally Identifiable User Generated Data while used in accordance with the preceding sentence.
                  </Paragraph>
               </li>
               <li>
                  <Paragraph>
                     <b>License Grant – Non-Personally Identifiable User Generated Data: </b>For the purposes of this EULA, “
                     <b>Non-Personally Identifiable User Generated Data</b>” means all User Generated Content except for Personally Identifiable User
                     Generated Data. You grant to NEUVANA a perpetual, irrevocable, non-exclusive, worldwide, royalty-free license, with the right to
                     sublicense, to reproduce, distribute, transmit, publicly perform, publicly display, digitally perform, modify, create derivative
                     works of, and otherwise use and commercially exploit Non-Personally Identifiable User Generated Data in any manner and at any
                     time. You waive any rights of publicity, moral or other similar such right with respect to the Non-Personally Identifiable User
                     Generated Data that might require your permission for NEUVANA to use such Non-Personally Identifiable User Generated Data.
                  </Paragraph>
               </li>
               <li>
                  <Paragraph>
                     <b>Intellectual Property: </b>The Software and NEUVANA Services are protected by copyright laws, international copyright,
                     patents, trademarks, trade secrets and other intellectual property rights. As between you and NEUVANA, NEUVANA retains all right,
                     title, interest, ownership and intellectual property rights in and to the Software and NEUVANA Services. The License confers no
                     title or ownership in the Software or NEUVANA Services and is not a sale of any rights in the Software or NEUVANA Services. The
                     License does not grant you any right to any enhancement or update to the Software and NEUVANA Services. NEUVANA reserves any and
                     all rights not expressly granted to you. The Software and/or NEUVANA Services may incorporate third party intellectual property
                     or open source code.
                  </Paragraph>
               </li>
               <li>
                  <Paragraph>
                     <b>Permitted Age: </b>You represent and warrant that: a) you are of legal age to form a binding contract with NEUVANA, and b) are
                     not barred from accessing the Software or NEUVANA Services under the laws of the United States, Canada, or other applicable
                     jurisdiction, including the country in which you reside or from where you download, install, copy, access or use the Software or
                     NEUVANA Services. By accepting this EULA you represent that you understand and agree to the foregoing.
                  </Paragraph>
               </li>
               <li>
                  <Paragraph>
                     <b>For Information Purposes Only: </b>Content generated or found through NEUVANA Services, Software or NEUVANA products is for
                     informational purposes only and is not intended to replace the relationship between you and your physician, healthcare provider
                     or other professional you are working with (collectively, “<b>Professional</b>”). NEUVANA’s Xen headband is not a medical device
                     and NEUVANA is not a licensed medical or healthcare provider and has no expertise in diagnosing, examining, or treating medical
                     conditions of any kind. Further, you understand that any information generated from NEUVANA Services, Software or NEUVANA
                     products is not medical information and you will ensure that medical information or other personal health information is not
                     uploaded to NEUVANA Services or Software or NEUVANA products. You assume all liability for any medical or health information that
                     is created or stored or uploaded on NEUVANA Services, Software or NEUVANA products, and you agree to fully indemnify and hold
                     NEUVANA harmless from any liability, expense, suit, action or claim (including regulatory proceedings) related to your breach of
                     this EULA including without limitation, any liability, expense, suit, action or claim that arises through your use of NEUVANA
                     Services, Software or NEUVANA products.
                  </Paragraph>
               </li>
               <li>
                  <Paragraph>
                     <b>Physical Symptoms: </b>In rare cases, people experience seizures or blackouts due to exposure to flashing lights and patterns
                     created by the display of certain applications on mobile or other such similar devices which may be used in conjunction with the
                     Software, NEUVANA Services or NEUVANA Products. If you have done so, or have experienced any nausea, involuntary movements,
                     tingling, numbness, or vision issues while using such devices in the past, you should consult with your doctor before using
                     similar applications and should immediately cease all such use of such applications should the symptoms re-occur. In any event
                     you should avoid prolonged use of such applications to minimize any possible discomfort or fatigue, including any muscle, joint
                     or eye strain and should closely monitor your children’s use of technology to avoid possible problems.
                  </Paragraph>
               </li>
               <li>
                  <Paragraph>
                     <b>Xen Connect Platform and/or Other Services: </b>If you wish to share the information generated through the Software and/or
                     NEUVANA Services with a third party such as a Professional, you may do so by providing your consent to such third party and/or
                     NEUVANA. You may take back or revoke your consent (unless NEUVANA has already acted based on it) by submitting a revocation
                     request in writing to NEUVANA through{' '}
                     <a href="http://www.chooseXen.com/contactsupport">http://www.chooseXen.com/contactsupport</a>, specifying that you revoke your
                     consent to share information generated through the Software and/or NEUVANA Services with the Professional. Your revocation will
                     take effect, when NEUVANA actually receives it. NEUVANA cannot give it retroactive effect, so it will not affect any use or
                     disclosure that occurred in NEUVANA’s reliance on your consent prior to revocation.
                  </Paragraph>
               </li>
            </OL>
            <li>
               <Paragraph>
                  <b>TERMINATION: </b>This EULA is effective until terminated. You may terminate this EULA at any time by uninstalling the Software
                  and destroying all copies of the Software in your possession or control and ceasing to use all associated NEUVANA Services. NEUVANA
                  may terminate this EULA immediately upon notice to you for any or no reason. The EULA will terminate immediately without notice from
                  NEUVANA if you fail to comply with any provision of this EULA (including breach for nonpayment, if applicable). Upon termination of
                  this EULA, you agree to immediately uninstall the Software and destroy all copies of the Software and cease using all associated
                  NEUVANA Services.
               </Paragraph>
            </li>
            <li>
               <Paragraph>
                  <b>LIMITED WARRANTIES BY NEUVANA: </b>
               </Paragraph>
               <OL>
                  <li>
                     <Paragraph>
                        <b>Statutory Protections: </b>THE APPLICABLE LAWS OF SOME COUNTRIES MAY NOT ALLOW THE EXCLUSION OF IMPLIED GUARANTEES,
                        ASSURANCES, CONDITIONS, WARRANTIES AND REPRESENTATIONS AND/OR THE LIMITATION OR EXCLUSION OF LIABILITY EITHER IN CONTRACTS OR
                        IF YOU ARE A CONSUMER, AND AS SUCH, SOME OR ALL OF THESE EXCLUSIONS MAY NOT APPLY TO YOU, IN WHICH CASE THIS AGREEMENT DOES
                        NOT AFFECT YOUR STATUTORY RIGHTS.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Limited Warranty: </b>NEUVANA DOES NOT WARRANT THAT THE SOFTWARE IS ERROR FREE. THE SOFTWARE AND NEUVANA SERVICES ARE
                        PROVIDED “AS IS” AND NEUVANA DISCLAIMS ALL GUARANTEES, ASSURANCES, CONDITIONS, WARRANTIES AND REPRESENTATIONS, WHETHER EXPRESS
                        OR IMPLIED, WITH RESPECT TO THE SOFTWARE AND NEUVANA SERVICES INCLUDING, BUT NOT LIMITED TO, THE WARRANTIES OF
                        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT OF THIRD PARTY RIGHTS.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Liability Limitation: </b>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, OTHER THAN LIABILITY FOR DEATH OR BODILY
                        INJURY RESULTING FROM NEUVANA’S GROSS NEGLIGENCE, NEUVANA, ITS SERVICE PROVIDERS AND ITS LICENSORS SHALL NOT BE LIABLE UNDER
                        ANY CONTRACT, NEGLIGENCE, STRICT LIABILITY OR OTHER LEGAL OR EQUITABLE THEORY FOR ANY CLAIMS ARISING FROM OR RELATED TO: (a)
                        THE INSTALLATION, DOWNLOAD, COPYING, ACCESS TO OR USE OF THE SOFTWARE OR THE ACCESS TO OR USE OF ANY NEUVANA SERVICE(S); OR
                        (b) THE INABILITY TO USE OR THE NON-PERFORMANCE OF THE SOFTWARE OR ANY NEUVANA SERVICE(S), IN ALL CASES WHETHER OR NOT SUCH
                        DAMAGES WERE FORESEEN OR UNFORESEEN, EVEN IF NEUVANA HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. FURTHER, TO THE
                        MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE AGGREGATE LIABILITY OF NEUVANA, ITS LICENSORS AND SERVICE
                        PROVIDERS EXCEED THE AMOUNT PAID BY YOU FOR THE SOFTWARE, OR THE APPLICABLE NEUVANA SERVICE, IN THE ONE (1) MONTH IMMEDIATELY
                        PRECEDING THE EVENT GIVING RISE TO YOUR CLAIM UNDER THIS EULA.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Maintenance and Support: </b>NEUVANA does provide minimal maintenance or support for it but not to the extent that any
                        maintenance or support is required by applicable law, NEUVANA, not Apple, shall be obligated to furnish any such maintenance
                        or support.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Basis of the Bargain; Exclusions: </b>The disclaimers of warranties and limitations of liability set forth above are
                        fundamental elements of the basis of the agreement between you and NEUVANA. You understand and agree that NEUVANA would not be
                        able to economically or reasonably provide the Software or the NEUVANA Services to you without these limitations.
                     </Paragraph>
                  </li>
               </OL>
            </li>
            <li>
               <Paragraph>
                  <b>GENERAL</b>
               </Paragraph>
               <OL>
                  <li>
                     <Paragraph>
                        <b>Governing Law: </b>Unless stated otherwise below, this EULA will be governed and construed in accordance with the laws of
                        the State of Florida, United States and in the event of a dispute, you agree to submit to the exclusive jurisdiction of the
                        courts of Palm Beach County in the State of Florida.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Privacy Policy: </b>You acknowledge and agree that you have read the NEUVANA Privacy Policy located at{' '}
                        <a href="http://www.neuvanalife.com/legal">http://www.neuvanalife.com/legal</a> (or otherwise made available to you through
                        NEUVANA Services) and consent to the handling and processing of your data as described in the NEUVANA Privacy Policy.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Language: </b>If this EULA is translated in a language other than English, to the extent of any conflict between the
                        English version and the translated version, the English version will prevail (
                        <a href="http://www.neuvanalife.com/legal">http://www.neuvanalife.com/legal</a>).
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Changes to EULA: </b>NEUVANA may change the terms and conditions of this EULA at any time and recommends that you review
                        them on a regular basis. You can review the most current version of this EULA at:{' '}
                        <a href="http://www.neuvanalife.com/legal">http://www.neuvanalife.com/legal</a> or through the NEUVANA Services. If NEUVANA
                        makes a change to the terms, it will post the revised version at the link above. You agree that your continued use of the
                        Software after this EULA has been changed means that you have agreed to the changed terms.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Accounts and User Conduct: </b>If NEUVANA determines that any user is not using the Software or NEUVANA Services
                        responsibly, NEUVANA has the right (but not the obligation) to remove, edit, block or delete such user’s transmissions, User
                        Generated Content, or use of the Software and/or NEUVANA Services. If NEUVANA, in its sole discretion, believes that the user
                        has engaged in or facilitated practices that: (i) are in breach of this EULA, (ii) are offensive, illegal or violate the
                        rights of others or NEUVANA, (iii) violate any terms of use or guidelines established by NEUVANA, or (iv) could result in
                        liability for NEUVANA, then NEUVANA has the right (but not the obligation), without notice, to terminate the user’s access to
                        and/or use of all or a portion of the Software and/or NEUVANA Services, or delete information posted or stored to the Software
                        and/or NEUVANA Services. In addition, NEUVANA reserves the right to limit the number of Xen accounts and the number of devices
                        associated with an account. If NEUVANA suspects that any registration data provided is inaccurate or incomplete, NEUVANA may
                        suspend or terminate the account without notice.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Entire Agreement; Amendment: </b>This EULA is the entire agreement with respect to the Software and NEUVANA Services and
                        supersedes any other agreements or discussions, oral or written.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Interpretation: </b>If any provision, clause or term of this EULA is determined by a court of competent jurisdiction to be
                        invalid or unenforceable, the invalidity or unenforceability of that provision, clause or term will not affect the validity or
                        enforceability of the remaining provisions, clauses and terms or the validity or enforceability of that provision, clause or
                        term in any other jurisdiction. If any provision of this EULA is declared by a court of competent jurisdiction to be invalid,
                        illegal or unenforceable, such provisions shall be severed from the EULA for the purposes of that jurisdiction and the other
                        provisions shall remain in full force and effect. No waiver by either party of a breach or omission by the other party to this
                        EULA will be binding on the waiving party unless it is expressly made in writing by the waiving party.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Survival: </b>The provisions of this EULA that under a commercially reasonable interpretation reveal that the parties would
                        have such provisions survive the termination of this EULA, including without limitation, Sections 1.2, 1.3, 1.4, 1.5, 1.7,
                        1.8, 1.9, 2, 3, and 4 will survive the termination of this EULA.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        <b>Other Terms: </b>Notwithstanding anything to the contrary contained herein or any products or services from NEUVANA,
                        NEUVANA and you acknowledge and agree to the following terms for the benefit of Apple Inc. and its subsidiaries and affiliates
                        (collectively, “Apple”): (a) “Made for iPod,” “Made for iPhone,” and “Made for iPad” mean that an electronic accessory has
                        been designed to connect specifically to iPod, iPhone, or iPad, respectively, and has been certified by the developer to meet
                        Apple performance standards. Apple is not responsible for the operation of this device or its compliance with safety and
                        regulatory standards. Please note that the use of this accessory with iPod, iPhone, or iPad may affect wireless performance;
                        (b) AirPlay, iPad, iPhone, iPod, iPod classic, iPod nano, iPod touch, and Retina are trademarks of Apple Inc., registered in
                        the U.S. and other countries. iPad Air, iPad mini, and Lightning are trademarks of Apple Inc.; All other trademarks and trade
                        names are the property of their respective owners and other company and product names mentioned herein may be trademarks of
                        their respective companies.
                     </Paragraph>
                  </li>
                  <li>
                     <Paragraph>
                        If you need any information about the Software or NEUVANA Services, you may consult the user documentation that is provided
                        with the Software or NEUVANA Services. If you wish to request customer support from NEUVANA for the Software or an NEUVANA
                        Service, or if you wish to provide Feedback about the Software or NEUVANA Services to NEUVANA, you may do so by contacting
                        NEUVANA at <a href="http://www.chooseXen.com/contactsupport">http://www.chooseXen.com/contactsupport</a> or at NEUVANA Inc.
                        2041 Vista Parkway, Suite #102, West Palm Beach FL 33431
                     </Paragraph>
                     <Paragraph>THIS EULA WAS LAST UPDATED ON NOVEMBER 13, 2019</Paragraph>
                  </li>
               </OL>
            </li>
         </OL>
      </TermsContainer>
   </ViewContainer>
);

export default TermsOfAgreement;

const PageHeader = styled.h2`
   font-weight: 600;
   color: ${({ theme }) => theme.primaryBlueDark};
   padding: 10px 0;
   margin: 2em auto 0.5em auto;
   text-align: center;
`;

const TermsContainer = styled.div`
   text-align: left;
   padding: 5px 10px;
   margin: 0 auto;
   line-height: 1.5;
   max-width: 700px;
   font-size: 1em;
   font-weight: 500;
   color: ${({ theme }) => theme.primaryBlueDark};
`;

const Paragraph = styled.p<{ bold?: boolean }>`
   display: block;
   font-weight: ${({ bold }) => (bold ? '700' : 'normal')};

   a {
      color: ${({ theme }) => theme.secondaryBlueDark};
   }
`;

const OL = styled.ol``;
